import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  XCircle,
  ExternalLink,
  Loader2,
  Receipt,
  Settings,
  Crown,
  Zap,
  Star,
  AlertCircle,
  ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const SubscriptionManagement = () => {
  const { token, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchSubscriptionData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/subscription/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(res.data.subscription);
      setPlan(res.data.plan);
    } catch (e) {
      console.warn('Error fetching subscription:', e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/subscription/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(res.data.transactions || []);
    } catch (e) {
      console.warn('Error fetching invoices:', e.message);
    }
  }, [token]);

  useEffect(() => {
    fetchSubscriptionData();
    fetchInvoices();
  }, [fetchSubscriptionData, fetchInvoices]);

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(
        `${API}/subscription/subscription/portal`,
        { return_url: window.location.href },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Customer Portal
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al abrir el portal');
      setPortalLoading(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar tu suscripción? Mantendrás el acceso hasta el final del período actual.')) {
      return;
    }

    try {
      await axios.post(
        `${API}/subscription/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Suscripción cancelada correctamente');
      fetchSubscriptionData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cancelar suscripción');
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'vip': return <Crown className="text-amber-400" size={24} />;
      case 'premium': return <Zap className="text-purple-400" size={24} />;
      default: return <Star className="text-blue-400" size={24} />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'vip': return 'from-amber-600 to-orange-600';
      case 'premium': return 'from-purple-600 to-pink-600';
      default: return 'from-blue-600 to-cyan-600';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Pagado</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Pendiente</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Fallido</Badge>;
      default:
        return <Badge className="bg-slate-500/20 text-slate-400">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 size={40} className="text-green-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      <Navbar />
      
      <main className="pt-20 px-4 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="subscription-title">
            Gestión de Suscripción
          </h1>
          <p className="text-slate-400">
            Administra tu plan, método de pago y revisa tu historial de facturas
          </p>
        </div>

        {/* Current Subscription Card */}
        <Card className="mb-6 overflow-hidden border-0" data-testid="current-subscription">
          <div className={`p-1 bg-gradient-to-r ${subscription ? getPlanColor(subscription.plan_id) : 'from-slate-600 to-slate-700'}`}>
            <div className="bg-slate-950 p-6 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${subscription ? getPlanColor(subscription.plan_id) : 'from-slate-600 to-slate-700'} flex items-center justify-center`}>
                    {subscription ? getPlanIcon(subscription.plan_id) : <XCircle className="text-slate-400" size={24} />}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      {plan ? plan.name : 'Sin Suscripción'}
                    </h2>
                    {subscription ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`${subscription.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                          {subscription.cancel_at_period_end ? 'Cancelado · acceso vigente' : subscription.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <span className="text-slate-500 text-sm">
                          {subscription.billing_cycle === 'yearly' ? 'Plan Anual' : 'Plan Mensual'}
                        </span>
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm mt-1">
                        No tienes una suscripción activa
                      </p>
                    )}
                  </div>
                </div>

                {subscription && (
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">
                      ${subscription.billing_cycle === 'yearly' ? (plan?.price_yearly || 0) : (plan?.price || 0)}
                      <span className="text-base font-normal text-slate-500">/{subscription.billing_cycle === 'yearly' ? 'año' : 'mes'}</span>
                    </p>
                  </div>
                )}
              </div>

              {subscription && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Calendar size={16} />
                      Fecha de Inicio
                    </div>
                    <p className="text-white font-semibold">
                      {formatDate(subscription.start_date)}
                    </p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <Clock size={16} />
                      Acceso hasta
                    </div>
                    <p className="text-white font-semibold">
                      {formatDate(subscription.end_date)}
                    </p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                      <CheckCircle2 size={16} />
                      Estado
                    </div>
                    <p className="text-green-400 font-semibold">
                      {subscription.cancel_at_period_end ? 'Cancelado; acceso vigente hasta la fecha indicada' : subscription.status === 'active' ? 'Activo' : 'Inactivo'}
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap gap-3">
                {subscription ? (
                  <>
                    <Button
                      onClick={openCustomerPortal}
                      disabled={portalLoading}
                      className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                      data-testid="manage-payment-btn"
                    >
                      {portalLoading ? (
                        <Loader2 size={18} className="animate-spin mr-2" />
                      ) : (
                        <CreditCard size={18} className="mr-2" />
                      )}
                      Gestionar Método de Pago
                    </Button>
                    {!subscription.cancel_at_period_end && <Button
                      variant="outline"
                      onClick={cancelSubscription}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid="cancel-subscription-btn"
                    >
                      <XCircle size={18} className="mr-2" />
                      Cancelar Suscripción
                    </Button>}
                  </>
                ) : (
                  <Link to="/pricing">
                    <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
                      <ArrowUpRight size={18} className="mr-2" />
                      Ver Planes
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Plan Features */}
        {plan && (
          <Card className="glass-card mb-6" data-testid="plan-features">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings size={20} className="text-emerald-400" />
                Características de tu Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                  <CheckCircle2 size={20} className="text-green-400" />
                  <div>
                    <p className="text-white font-medium">Loterías</p>
                    <p className="text-slate-400 text-sm">
                      {plan.features?.max_lotteries === 'unlimited' 
                        ? 'Acceso ilimitado' 
                        : `Hasta ${plan.features?.max_lotteries} loterías`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                  <CheckCircle2 size={20} className="text-green-400" />
                  <div>
                    <p className="text-white font-medium">Recomendaciones Diarias</p>
                    <p className="text-slate-400 text-sm">
                      {plan.features?.daily_recommendations === 'unlimited'
                        ? 'Ilimitadas'
                        : `${plan.features?.daily_recommendations} por día`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                  <CheckCircle2 size={20} className="text-green-400" />
                  <div>
                    <p className="text-white font-medium">Estrategias</p>
                    <p className="text-slate-400 text-sm">
                      {plan.features?.strategies?.join(', ') || 'Básica'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                  {plan.features?.super_ia ? (
                    <CheckCircle2 size={20} className="text-green-400" />
                  ) : (
                    <XCircle size={20} className="text-slate-500" />
                  )}
                  <div>
                    <p className="text-white font-medium">Sócrates</p>
                    <p className="text-slate-400 text-sm">
                      {plan.features?.super_ia ? 'Incluido' : 'No incluido'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        <Card className="glass-card" data-testid="payment-history">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Receipt size={20} className="text-emerald-400" />
              Historial de Pagos
            </CardTitle>
            <CardDescription className="text-slate-400">
              Tus últimas transacciones y facturas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.map((tx, idx) => (
                  <div 
                    key={tx.id || idx}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50 border border-slate-800"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Receipt size={18} className="text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{tx.plan_name}</p>
                        <p className="text-slate-500 text-sm">
                          {formatDate(tx.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-white font-bold">${tx.amount?.toFixed(2)}</p>
                        <p className="text-slate-500 text-xs uppercase">{tx.currency}</p>
                      </div>
                      {getStatusBadge(tx.payment_status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Receipt size={48} className="mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400">No hay transacciones todavía</p>
                <p className="text-slate-500 text-sm">
                  Tu historial de pagos aparecerá aquí
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="glass-card mt-6 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">¿Necesitas ayuda?</p>
                <p className="text-slate-400 text-sm">
                  Si tienes problemas con tu suscripción o pagos, contacta a soporte en{' '}
                  <a href="mailto:soporte@numeromaestro.com" className="text-emerald-400 hover:underline">
                    soporte@numeromaestro.com
                  </a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <DisclaimerBanner variant="fixed" />
    </div>
  );
};

export default SubscriptionManagement;
